import React, { useEffect, useState } from 'react';
import { Control, Controller, ControllerFieldState, ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import { FormField, Input } from '@legalshield/adonis-ux-framework';

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
};

const insertSlashes = (digits: string): string => {
  const d = digits.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const parseDisplay = (display: string): { iso: string; ymd: string } | null => {
  const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const [, mm, dd, yyyy] = match;
  const y = Number(yyyy);
  const m = Number(mm);
  const d = Number(dd);
  const localMidnight = new Date(y, m - 1, d);
  if (localMidnight.getFullYear() !== y || localMidnight.getMonth() + 1 !== m || localMidnight.getDate() !== d) {
    return null;
  }
  return {
    iso: localMidnight.toISOString(),
    ymd: `${yyyy}-${mm}-${dd}`,
  };
};

const isoToDisplay = (value: string | undefined): string => {
  if (!value) return '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const [, yyyy, mm, dd] = match;
  return `${mm}/${dd}/${yyyy}`;
};

const todayYmd = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

type InnerProps<T extends FieldValues> = {
  field: ControllerRenderProps<T, Path<T>>;
  fieldState: ControllerFieldState;
  name: Path<T>;
  label: string;
  required?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
};

const DateInner = <T extends FieldValues>({
  field,
  fieldState,
  name,
  label,
  required,
  disablePast,
  disableFuture,
}: InnerProps<T>) => {
  const [display, setDisplay] = useState<string>(isoToDisplay(field.value as string | undefined));
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setDisplay(isoToDisplay(field.value as string | undefined));
  }, [field.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const formatted = insertSlashes(raw);
    setDisplay(formatted);
    setLocalError(null);

    if (formatted.length !== 10) {
      if ((field.value as string | undefined) !== '') {
        field.onChange('');
      }
      return;
    }

    const parsed = parseDisplay(formatted);
    if (!parsed) {
      setLocalError('Invalid Date');
      field.onChange('');
      return;
    }

    const today = todayYmd();
    if (disableFuture && parsed.ymd > today) {
      setLocalError('Future Dates Are Not Valid');
      field.onChange('');
      return;
    }
    if (disablePast && parsed.ymd < today) {
      setLocalError('Past Dates Are Not Valid');
      field.onChange('');
      return;
    }

    field.onChange(parsed.iso);
  };

  const status = fieldState.error || localError ? 'invalid' : undefined;
  const hint = localError ?? fieldState.error?.message ?? undefined;

  return (
    <FormField id={String(name)} label={label} required={required} status={status} validationHint={hint}>
      <Input
        placeholder="MM/DD/YYYY"
        type="text"
        value={display}
        onChange={handleChange}
        onBlur={field.onBlur}
        maxLength={10}
        status={status}
      />
    </FormField>
  );
};

const DateField = <T extends FieldValues>({ control, name, label, required, disablePast, disableFuture }: Props<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <DateInner
        field={field}
        fieldState={fieldState}
        name={name}
        label={label}
        required={required}
        disablePast={disablePast}
        disableFuture={disableFuture}
      />
    )}
  />
);

export default DateField;
